/**
 * @file DeliveryActionsPanel.tsx
 * @description Правая колонка блока «Доставка» под «Итогом»: порядок оплаты,
 * предпросмотр письма и действия (phase19.24 шаг 5.2, §3).
 *
 * Правило DS §5.3 здесь тоже действует: ровно одна главная кнопка
 * (её подпись приходит из реестра состояний — «Отправить на подтверждение»
 * или «Отправить на повторное согласование»), «Сохранить черновик» — рядом
 * вторичной, а редкие и опасные действия — в меню «⋯». Причина, по которой
 * отправить нельзя, печатается рядом с кнопкой, а не прячется в подсказке.
 */

import { Save, XCircle } from "lucide-react";

import type { OrderPaymentTerms } from "../../../lib/api-orders";
import type { ShopPickupLocationDto } from "../../../lib/api-shop-pickup";
import type { ReceiptFormValue } from "../../../lib/receipt-method";
import { Button } from "../../ui/button";
import { RowActionsMenu } from "../../ui/RowActionsMenu";
import { DeliveryAgreementPreview } from "../DeliveryAgreementPreview";
import { OrderPaymentTermsField } from "../OrderPaymentTermsField";

interface Props {
  value: ReceiptFormValue;
  paymentTerms: OrderPaymentTerms;
  onPaymentTermsChange: (next: OrderPaymentTerms) => void;
  /** Клиент — юрлицо: оплата идёт счётом. */
  legalEntity: boolean;
  itemsTotal: string;
  pickup: ShopPickupLocationDto | null;
  /** Подпись главной кнопки из реестра состояний блока. */
  primaryLabel: string;
  /** Почему отправить нельзя; `null` — можно. */
  blockedReason: string | null;
  /** Заявка уже существует — её можно отменить (меню «⋯»). */
  canCancel: boolean;
  disabled: boolean;
  onPrimary: () => void;
  onSaveDraft: () => void;
  onCancelRequest: () => void;
}

export function DeliveryActionsPanel({
  value,
  paymentTerms,
  onPaymentTermsChange,
  legalEntity,
  itemsTotal,
  pickup,
  primaryLabel,
  blockedReason,
  canCancel,
  disabled,
  onPrimary,
  onSaveDraft,
  onCancelRequest,
}: Props) {
  return (
    <div className="flex flex-col gap-4">
      <OrderPaymentTermsField
        method={value.method}
        value={paymentTerms}
        onChange={onPaymentTermsChange}
        legalEntity={legalEntity}
        disabled={disabled}
        idPrefix="delivery"
      />

      <DeliveryAgreementPreview
        value={value}
        paymentTerms={paymentTerms}
        itemsTotal={itemsTotal}
        pickup={pickup}
      />

      {blockedReason ? <p className="text-sm text-warning">{blockedReason}</p> : null}

      <div className="flex flex-col gap-2">
        {/* Главная кнопка первой и во всю ширину: на 360px до неё дотягиваются
            большим пальцем, не перебирая вторичные действия. */}
        <Button
          className="min-h-14 w-full lg:min-h-12"
          disabled={disabled || !!blockedReason}
          onClick={onPrimary}
        >
          {primaryLabel}
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            className="min-h-14 flex-1 lg:min-h-12"
            disabled={disabled}
            onClick={onSaveDraft}
          >
            <Save className="mr-1 size-4" aria-hidden />
            Сохранить черновик
          </Button>
          {canCancel ? (
            <RowActionsMenu
              title="Действия с заявкой"
              ariaLabel="Другие действия с заявкой"
              disabled={disabled}
              actions={[
                // Деструктивное действие — последним (правило A6).
                {
                  key: "cancel",
                  label: "Отменить заявку",
                  icon: XCircle,
                  danger: true,
                  run: onCancelRequest,
                },
              ]}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}
